import { Container, Graphics, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"
import {
    DIAL_VALUE_Y,
    DialLabel,
    DialMetrics,
    dialArcRadius,
    dialBottomExtent,
    dialFaceRadius,
    dialSize,
    drawDialFace,
    drawDialGauge,
    drawDialHeat,
    easeOutCubic,
    interpolateColor,
} from "./dial"

// The HUD dial that counts DOWN to something the player has to be ready for — in an ember camp, the
// next assault landing on the fire.
//
// It shares its chrome and its screen slot with the WAVE BADGE (wave_indicator.ts): this dial drains
// to zero and vanishes, and the badge grows into the same spot already full, so the HUD reads as one
// object changing state rather than two widgets taking turns. The shared parts live in dial.ts; what
// is this widget's own is the PALETTE (banked coals, flaring, white-hot) and the SECOND —
// every whole second the numeral punches in and a ring ripples off the dial, which is what makes a
// countdown feel like it is running rather than merely being displayed.
//
// The numeral carries its UNIT, and that is the whole of the bottom line. Without the unit the dial's
// biggest element was a bare number over the word "INCOMING", which reads as a headcount — eleven
// enemies on the way rather than eleven seconds — and a wave-defense HUD can afford that confusion
// least of all on the one object the player watches. With it, "WAVE 20" over "3s" says everything
// the caption used to, in two lines instead of three: "INCOMING" measured 68px wide against a ring
// whose inside is 85px across, so it cut through the gauge at every size that was worth reading.
//
// Everything animates off the frame delta handed to `update`, so the widget never owns a timer. The
// caller only has to stop calling it when it is no longer `visible`.

// Entrance and exit, in ms. The exit is quicker: by then the thing it was counting to has arrived
// and is what the player should be looking at.
const APPEAR_MS = 190
const VANISH_MS = 240

// How long a fresh numeral takes to settle back from its punch-in, and how much bigger it starts.
const TICK_MS = 260
const TICK_SWELL = 0.3

// The ring that ripples off the dial on each whole second, and how far past the dial's edge it gets.
// It starts OUTSIDE the rim and leaves immediately: a ring sitting ON the dial reads as the gauge,
// which at one second left said "full" at the exact moment the gauge said "empty".
const RIPPLE_MS = 420
const RIPPLE_REACH = 15

// Inside this many seconds the dial is urgent: the glow breathes, the face heats from within and
// the ripples read as an alarm rather than a tick.
const URGENT_SECONDS = 5

// Gauge ramp: banked coals while there is time, flaring as it burns down, white-hot at the end.
// Deliberately NOT a traffic light — green-amber-red is a dashboard's vocabulary, and the moment a
// siege warning borrows it the dial becomes a stopwatch with a progress bar. Cold-to-hot carries the
// same urgency and belongs to a camp that lives by its fire.
export const COUNTDOWN_BANKED = 0xc2703a
export const COUNTDOWN_FLARE = 0xff9d2e
export const COUNTDOWN_HOT = 0xffe9a8

// What the face itself heats to under the numerals, as the ring goes white.
const FORGE_COLOR = 0xd6391f

// The unit's colour: the same warm cream the badge captions itself in.
const CAPTION_COLOR = 0xffc89a

// The unit riding on the numeral's right. Big enough to read at a glance, small and quiet enough
// that it can never be mistaken for a second digit.
const UNIT_TEXT = "s"
const UNIT_SIZE = TEXT_ROLES.compactBody.fontSize
const UNIT_ALPHA = 0.82
const UNIT_GAP = 2

export interface CountdownTimerProps {
    radius?: number
    strokeWidth?: number
    backgroundColor?: number
    textColor?: number
    onComplete?: () => void
}

/** Banked coals (plenty of time) -> flaring (half) -> white-hot (out of time). */
export const countdownColor = (progress: number): number => {
    if (progress > 0.5) {
        return interpolateColor(COUNTDOWN_FLARE, COUNTDOWN_BANKED, (progress - 0.5) / 0.5)
    }
    return interpolateColor(COUNTDOWN_HOT, COUNTDOWN_FLARE, Math.max(0, progress) / 0.5)
}

export class CountdownTimer extends Container {
    private metrics: DialMetrics
    private backgroundColor?: number
    private textColor: number
    private onComplete?: () => void

    private face: Graphics
    private fx: Graphics
    private gauge: Graphics
    // The numeral and its unit move as one: the punch-in scales this, so the "s" never collides with
    // a swelling digit and never drifts away from it either.
    private valueGroup: Container
    private textDisplay: Text
    private unitText: Text
    private label: DialLabel

    private currentTime: number = 0
    private totalTime: number = 0
    private isRunning: boolean = false

    // What the numeral currently reads, so a second that hasn't flipped costs no re-rasterization.
    private lastDisplayTime: number = -1
    // The wave this is counting down to, when the camp told us. Zero means a bare timer: no wave
    // label, and no "INCOMING" either, because then there is no assault for it to be about.
    private wave: number = 0
    private expedition = false

    // Whether the dial wants to be on screen, and how far the entrance/exit has got (0..1).
    private wanted: boolean = false
    private appear: number = 0
    // Age of the current numeral, in ms. Drives its punch-in and the ripple behind it.
    private tickAge: number = TICK_MS
    // Free-running clock for the urgent breathing, so it doesn't stall when the arc does.
    private pulseTs: number = 0

    constructor(props: CountdownTimerProps = {}) {
        super()

        this.metrics = { radius: props.radius || 50, strokeWidth: props.strokeWidth || 8 }
        // Kept for callers that still pass one, but the ember dials share a face now: an override
        // would be the one thing that breaks the hand-over to the wave badge.
        this.backgroundColor = props.backgroundColor
        this.textColor = props.textColor || 0xffffff
        this.onComplete = props.onComplete

        this.face = new Graphics()
        this.fx = new Graphics()
        this.gauge = new Graphics()
        this.valueGroup = new Container()

        this.textDisplay = new TextRenderer().color(this.textColor).role("heading").bold().align("center").render("0") as Text
        this.textDisplay.anchor.set(0.5)
        this.unitText = new TextRenderer()
            .color(CAPTION_COLOR)
            .fontSize(UNIT_SIZE)
            .bold()
            .align("center")
            .render(UNIT_TEXT) as Text
        // Anchored on its LEFT edge and vertically centred, so it hangs off the numeral's right no
        // matter how many digits the numeral has.
        this.unitText.anchor.set(0, 0.5)
        this.unitText.alpha = UNIT_ALPHA
        this.valueGroup.addChild(this.textDisplay, this.unitText)
        this.label = new DialLabel()

        // The ripple goes BEHIND the gauge so an alarm ring never washes out the arc it came from.
        this.addChild(this.face, this.fx, this.gauge, this.label, this.valueGroup)

        this.drawFace()
        this.layoutText()

        this.visible = false
        this.alpha = 0
    }

    // ---------------------------------------------------------------- geometry

    /** Outer painted size of the dial itself — what a caller should position against. */
    public get dialSize(): number {
        return dialSize(this.metrics)
    }

    /** How far below the dial's CENTRE the widget paints. */
    public get bottomExtent(): number {
        return dialBottomExtent(this.metrics)
    }

    // ---------------------------------------------------------------- control

    /**
     * Start counting down.
     *
     * `wave` is the assault this is counting to, when the camp knows it: it heads the dial with the
     * same line the wave badge will be wearing a moment later, in the same place, so the hand-over
     * leaves it untouched. Omit it for a bare timer.
     */
    public start(totalSeconds: number, wave: number = 0, expedition = false) {
        this.expedition = expedition
        this.totalTime = totalSeconds
        this.currentTime = totalSeconds
        this.isRunning = true
        this.wave = wave > 0 ? Math.floor(wave) : 0
        this.lastDisplayTime = -1
        // The entrance carries the first numeral in; punching it as well reads as a stutter.
        this.tickAge = TICK_MS
        this.wanted = true
        this.appear = 0
        this.visible = true
        this.alpha = 0

        this.redraw()
    }

    public reset() {
        this.currentTime = this.totalTime
        this.isRunning = false
        this.lastDisplayTime = -1
        this.tickAge = TICK_MS
        this.wanted = true
        this.appear = 1
        this.alpha = 1
        this.visible = true
        this.scale.set(1)
        this.redraw()
    }

    /** Halt without finishing. The dial fades out; a caller that hides it outright also may. */
    public stop() {
        this.isRunning = false
        this.wanted = false
    }

    public getCurrentTime(): number {
        return this.currentTime
    }

    public getTotalTime(): number {
        return this.totalTime
    }

    public isActive(): boolean {
        return this.isRunning
    }

    /** Test/inspection seam: how far the entrance (or exit) has got, 0..1. */
    public appearance(): number {
        return this.appear
    }

    // ---------------------------------------------------------------- frame

    /**
     * Advance one frame. `deltaTime` is in milliseconds.
     *
     * Always does its animation work, even once the count has reached zero: the exit is part of the
     * countdown, and a widget that stopped moving the instant it hit zero would be yanked away at
     * the one moment the player is watching it.
     */
    public update(deltaTime: number) {
        if (this.isRunning) {
            this.currentTime -= deltaTime / 1000
            if (this.currentTime <= 0) {
                this.currentTime = 0
                this.isRunning = false
                this.onComplete?.()
                // Zero is reached, so the dial has said everything it has to say.
                this.wanted = false
            }
        }

        const target = this.wanted ? 1 : 0
        if (this.appear !== target) {
            const step = deltaTime / (this.wanted ? APPEAR_MS : VANISH_MS)
            this.appear = target > this.appear ? Math.min(1, this.appear + step) : Math.max(0, this.appear - step)
        }

        this.tickAge += deltaTime
        this.pulseTs += deltaTime

        if (!this.wanted && this.appear <= 0) {
            this.visible = false
            this.alpha = 0
            return
        }

        this.redraw()
    }

    // ---------------------------------------------------------------- drawing

    private redraw() {
        const eased = easeOutCubic(this.appear)
        this.alpha = eased
        // Grows into place and shrinks away rather than popping — the wave badge that replaces it
        // arrives the same way, from the same spot.
        this.scale.set(0.84 + 0.16 * eased)

        const displayTime = Math.ceil(this.currentTime)
        if (displayTime !== this.lastDisplayTime) {
            this.lastDisplayTime = displayTime
            this.textDisplay.text = String(Math.max(0, displayTime))
            this.tickAge = 0
            // 9 -> 10 is a wider numeral, so the unit beside it has to move with it.
            this.layoutValue()
        }

        const progress = this.totalTime > 0 ? Math.max(0, Math.min(1, this.currentTime / this.totalTime)) : 0
        const color = countdownColor(progress)
        const urgency = this.urgency()

        // A fresh numeral punches in and settles back. `tickAge` is wall-clock, not the fraction of
        // the remaining second, so the swell lasts the same 260ms whatever the frame rate — and it
        // happens when the number CHANGES, which is the thing the player is reacting to.
        const tick = 1 - easeOutCubic(Math.min(1, this.tickAge / TICK_MS))
        // The whole value — numeral and unit — swells about the numeral's own centre, which is the
        // group's origin. Scaling the numeral alone would have it grow INTO the unit and back out.
        this.valueGroup.scale.set(1 + TICK_SWELL * tick)
        // Warm the numeral toward the ring colour as time drains: crisp white while there is plenty,
        // reddening for urgency near zero. A tint is a cheap multiply over white text, so it never
        // re-rasterizes the glyphs — but it can only ever DARKEN, so the mix is capped well short of
        // the ring's own colour. Taking it the whole way left the last few seconds, the ones that
        // actually have to be read, as dim red numerals on a dark red face.
        this.textDisplay.tint = interpolateColor(0xffffff, color, Math.min(0.5, Math.pow(1 - progress, 1.6)))

        this.drawRipple(color, urgency)
        this.drawHeat(urgency)
        this.drawGauge(progress, color, urgency)
        this.layoutLabel()
    }

    private urgency(): number {
        if (!(this.totalTime > 0) || this.currentTime <= 0) {
            return 0
        }
        const window = Math.min(URGENT_SECONDS, this.totalTime)
        return Math.max(0, Math.min(1, (window - this.currentTime) / window))
    }

    private drawFace() {
        drawDialFace(this.face, { ...this.metrics, faceColor: this.backgroundColor })
    }

    // One expanding ring per second. Cheap (a single stroked circle) and it is what carries the
    // tick: the dial visibly emits something every time the number changes.
    private drawRipple(color: number, urgency: number) {
        this.fx.clear()
        const age = this.tickAge / RIPPLE_MS
        if (age >= 1 || this.currentTime <= 0) {
            return
        }
        const grow = easeOutCubic(age)
        const radius = dialFaceRadius(this.metrics) + 1 + RIPPLE_REACH * grow
        this.fx.lineStyle({ width: 2 + urgency, color, alpha: (0.26 + 0.2 * urgency) * Math.pow(1 - grow, 1.5) })
        this.fx.drawCircle(0, 0, radius)
    }

    // The face heats from within over the final seconds, breathing with the glow — a forge coming
    // up, not a warning lamp switching on. Drawn over the face rather than into it so the face
    // itself stays a one-off draw.
    private drawHeat(urgency: number) {
        if (urgency <= 0) {
            return
        }
        const breath = 0.5 + 0.5 * Math.sin(this.pulseTs / 170)
        drawDialHeat(this.fx, this.metrics, FORGE_COLOR, urgency * (0.78 + 0.22 * breath))
    }

    private drawGauge(progress: number, color: number, urgency: number) {
        if (this.currentTime <= 0) {
            this.gauge.clear()
            return
        }
        const breath = urgency > 0 ? 0.5 + 0.5 * Math.sin(this.pulseTs / 170) : 0
        drawDialGauge(this.gauge, {
            ...this.metrics,
            progress,
            color,
            haloWidth: 5 + 10 * urgency * (0.55 + 0.45 * breath),
            haloAlpha: 0.22 + 0.4 * urgency * (0.6 + 0.4 * breath),
        })
    }

    // Which wave, then the number — on the lines dial.ts fixes, because the wave badge stacks itself
    // on the same ones (it adds a third, "LEFT", which is short enough to fit where "INCOMING" was
    // not). DialLabel parks itself on the top line, so only the value is set here.
    private layoutText() {
        this.valueGroup.y = DIAL_VALUE_Y
        this.layoutValue()
        this.layoutLabel()
    }

    // Park the unit off the numeral's right and shift the pair left by half of what it added, so
    // what the eye centres on is the whole value rather than the digits alone. Measured unscaled:
    // the swell lives on the group, so the numeral's own scale is always 1.
    private layoutValue() {
        const advance = this.unitText.width + UNIT_GAP
        this.unitText.x = this.textDisplay.width / 2 + UNIT_GAP
        this.valueGroup.x = -advance / 2
    }

    private layoutLabel() {
        // A bare timer (`/timer`) has no wave to name: it is just a clock, so it shows its number.
        this.label.setWave(this.wave, this.expedition ? "NEXT" : "WAVE")
    }
}

// The arc the gauge is stroked along. Exported for the layout tests, which assert the countdown dial
// and the wave badge share one geometry.
export const countdownArcRadius = (metrics: DialMetrics): number => dialArcRadius(metrics)
